import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  ClickAwayListener,
  Grow,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@mui/material'
import { Delete, EditOutlined, FileDownloadOutlined, MoreVert } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'

// Historia 148 — el menú de acciones secundarias de una fila, con la MISMA gramática visual que el
// kebab de `generic/ActionsMenu.jsx`, que es el que usan las tablas del resto de MIC: `MoreVert`
// sobre `primary.dark` al 80 %, `Popper` + `Grow` + `Paper` + `MenuList`, y cada entrada como icono
// + etiqueta.
//
// ⚠️ POR QUÉ NO SE MONTA `ActionsMenu` DIRECTAMENTE: ese componente es una pieza de
// `SmartMateriaUITable` y recibe su maquinaria —`editIndex`, `setEditIndex`, `dataFetch`,
// `requestPagination`, `smartTableIndexKey`, `multipleSelectionValue`, `setOpenDeleteDialog`…—.
// Montarlo aquí obligaría a fabricar una tabla falsa alrededor de cada fila. Lo que se reutiliza es
// su LENGUAJE, no su acoplamiento.
//
// ⚠️ La acción de VER no entra aquí: RN31/S55 exige que abrir el documento sea la acción principal
// y esté visualmente destacada, no escondida tras un menú. Ver se queda en la fila (y además toda
// la fila abre el documento); lo secundario —descargar, editar, eliminar— se recoge en el kebab,
// que es lo que libera el ancho que el nombre del documento necesita.

const ACTION_BUTTON_SX = (theme) => ({
  backgroundColor: theme.palette.primary.dark,
  opacity: 0.8,
  padding: '5px',
  minWidth: 40,
  minHeight: 40
})

const FabricationDocumentRowActions = ({
  entry,
  source,
  permissions,
  onDownload = () => {},
  onEdit = () => {},
  onDelete = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  // RN16/S55: solo las acciones que el grupo concede. Una acción no concedida no se pinta en gris:
  // sencillamente no está en el menú.
  const items = [
    { granted: permissions.download, label: 'Download file', icon: <FileDownloadOutlined fontSize='small' />, run: onDownload },
    { granted: permissions.edit, label: 'Edit', icon: <EditOutlined fontSize='small' />, run: onEdit },
    { granted: permissions.delete, label: 'Delete', icon: <Delete fontSize='small' />, run: onDelete }
  ].filter((item) => item.granted)

  if (items.length === 0) {
    return null
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-label={t_('Actions')}
        onClick={(event) => { event.stopPropagation(); setOpen((previous) => !previous) }}
        sx={ACTION_BUTTON_SX(theme)}
      >
        <MoreVert />
      </IconButton>

      <Popper open={open} anchorEl={anchorRef.current} transition placement='bottom-end' sx={{ zIndex: 9999 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper sx={{ zIndex: 9999 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} sx={{ padding: 0, '& .MuiMenuItem-root': { pl: 1, pr: 2, minHeight: 48 } }}>
                  {items.map((item) => (
                    <MenuItem
                      key={item.label}
                      aria-label={t_(item.label)}
                      onClick={(event) => {
                        event.stopPropagation()
                        setOpen(false)
                        item.run(entry, source)
                      }}
                    >
                      <IconButton component='span' disableRipple>{item.icon}</IconButton>
                      <ListItemText primary={t_(item.label)} />
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

FabricationDocumentRowActions.propTypes = {
  entry: PropTypes.object.isRequired,
  source: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  onDownload: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
}

export default FabricationDocumentRowActions
